import { createContext, useCallback, useContext } from 'react';
import {
  CloseButtonProps,
  toast,
  ToastContainer,
  ToastOptions,
  ToastPosition,
} from 'react-toastify';
import CloseButton from '@c/icons/buttons/CloseButton';

interface ToastContext {
  showToast: (content: JSX.Element, options?: ToastOptions<Record<string, unknown>>) => void;
  showSuccessToast: (text: string, position?: ToastPosition) => void;
  showErrorToast: (text: string, position?: ToastPosition) => void;
}
const ToastContext = createContext({} as ToastContext);
export function useToastContext() {
  return useContext(ToastContext);
}

// GB #1817. react-toastify supplies the dismiss handler to whatever is passed
// as `closeButton`: an element is re-created with `cloneElement(el, {
// closeToast, type, theme })`, a function component is called with the same
// object. The previous signature here was `() => (...)`, which declared no
// props at all, so `closeToast` was silently discarded and the X was wired to
// nothing on EVERY toast in the app. An operator was left staring at a
// permanent money-path banner with no way to clear it.
//
// The props are `Partial<>` because these call sites write the plain element
// `<CustomCloseButton />`: TypeScript checks that JSX where it is written,
// which is before react-toastify clones the real props in.
//
// `stopPropagation` mirrors react-toastify's own default close button, and is
// load-bearing rather than defensive. The toast's wrapper div carries a click
// handler whenever the caller passed an `onClick` option or left `closeOnClick`
// on, so without stopping the bubble a click on the X would ALSO run that
// caller's action — navigate, retry, mutate — instead of only dismissing.
const CustomCloseButton = ({ closeToast }: Partial<CloseButtonProps>) => {
  return (
    <div className="ml-[1.6rem] mr-[1.6rem] flex flex-col items-center justify-center">
      <CloseButton
        color="brand-secondary"
        onClick={(event) => {
          event.stopPropagation();
          closeToast?.(event);
        }}
      />
    </div>
  );
};
export function ToastProvider({ children }: any) {
  const showToast = useCallback(
    (content: JSX.Element, options?: ToastOptions<Record<string, unknown>> | undefined) => {
      toast(content, options);
    },
    []
  );

  const showSuccessToast = (
    text: string,
    position: ToastPosition = 'top-center'
  ) => {
    toast.success(text, {
      position,
      autoClose: 1500,
      hideProgressBar: true,
      closeOnClick: true,
      rtl: false,
      pauseOnFocusLoss: true,
      draggable: true,
      pauseOnHover: true,
      theme: 'light',
      closeButton: <CustomCloseButton />,
    });
  };

  const showErrorToast = (
    text: string,
    position: ToastPosition = 'top-center'
  ) => {
    toast.error(text, {
      position,
      autoClose: 1500,
      hideProgressBar: true,
      closeOnClick: true,
      rtl: false,
      pauseOnFocusLoss: true,
      draggable: true,
      pauseOnHover: true,
      theme: 'light',
      closeButton: <CustomCloseButton />,
    });
  };

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccessToast,
        showErrorToast,
      }}
    >
      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={<CustomCloseButton />}
      />
      {children}
    </ToastContext.Provider>
  );
}
